'use client';

import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { NdrCompanyCardSkeleton } from '@/app/_components/NDR/NdrCompanyCard';
import { NdrFaq } from '@/app/_components/NDR/NdrFaq';
import { NdrFooter } from '@/app/_components/NDR/NdrFooter';
import { NdrHeader } from '@/app/_components/NDR/NdrHeader';
import { NdrHero } from '@/app/_components/NDR/NdrHero';
import { NdrPopup } from '@/app/_components/NDR/NdrPopup';
import { NdrStickyBarSkeleton } from '@/app/_components/NDR/NdrStickyBar';
import { TableOfContents } from '@/app/_components/NDR/TableOfContents';
import { detectTrafficSource, getNdrHomeUrl, getQueryParamsString, isNdrLink } from '@/app/_lib/utils/ndrUtils';
import { cn } from '@/app/_lib/utils/utils';
import { ndrData } from '@/app_data/mock/ndrData';
import { DescriptionParser } from '@/app_lib/htmlParser/DescriptionParser';

const NdrStickyBar = dynamic(() => import('@/app/_components/NDR/NdrStickyBar').then((mod) => mod.NdrStickyBar), {
  loading: () => <NdrStickyBarSkeleton />,
  ssr: false,
});

const NdrCompanyCard = dynamic(() => import('@/app/_components/NDR/NdrCompanyCard').then((mod) => mod.NdrCompanyCard), {
  loading: () => <NdrCompanyCardSkeleton />,
  ssr: false,
});

interface NdrWrapperContentProps {
  children?: React.ReactNode;
  source?: 'google' | 'bing';
}

interface RetreaverCampaign {
  // biome-ignore lint/style/useNamingConvention: required by Retreaver
  auto_replace_numbers: () => void;
}

const NdrWrapper = ({ children, source }: NdrWrapperContentProps) => {
  const [stickyBarMounted, setStickyBarMounted] = useState(false);
  const [companyCardMounted, setCompanyCardMounted] = useState(false);
  const retreaverCampaign = useRef<RetreaverCampaign | null>(null);
  const retreaverWaiting = useRef<boolean>(false);
  const stickyTriggerRef = useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>;

  const searchParams = useSearchParams();
  const allQueryParamsString = getQueryParamsString(searchParams, source);
  const isGoogleSource = detectTrafficSource(searchParams) === 'google' || source === 'google';
  const homeUrl = getNdrHomeUrl(isGoogleSource ? 'google' : 'bing');

  const data = isGoogleSource ? ndrData.landingPages.google : ndrData.landingPages.bing;
  const enabledData = data.reviews.filter((review) => review.ctas.primary.url !== '');
  const orderedReviews = enabledData.sort((a, b) => a.ordinal - b.ordinal);
  const stickyBarData = enabledData.find((item) => item.sticky === true);

  const runRetreaver = useCallback(() => {
    const performAction = () => {
      if (retreaverCampaign.current === null) {
        window.Retreaver.configure({
          host: 'api.calltrackapi.com',
          prefix: 'https',
        });

        retreaverCampaign.current = new window.Retreaver.Campaign({
          // biome-ignore lint/style/useNamingConvention: required by Retreaver
          campaign_key: 'c0ff9c04926762b90d3201cb7444c95b',
        }) as RetreaverCampaign;
      }

      retreaverCampaign.current.auto_replace_numbers();
    };

    if (retreaverWaiting.current) {
      return;
    }

    if (window.Retreaver === undefined) {
      const retreaveInterval = 100;
      retreaverWaiting.current = true;
      const intvl = setInterval(() => {
        if (window.Retreaver !== undefined) {
          retreaverWaiting.current = false;
          clearInterval(intvl);
          performAction();
        }
      }, retreaveInterval);
    } else {
      performAction();
    }
  }, []);

  useEffect(() => {
    if (!(stickyBarMounted && companyCardMounted)) {
      return;
    }

    runRetreaver();
  }, [stickyBarMounted, companyCardMounted, runRetreaver]);

  if (!stickyBarData) {
    return null;
  }

  return (
    <div
      className={cn(
        'ndr-page-wrapper flex flex-col layout-ndr-gap layout-sticky-safe-area',
        isGoogleSource ? 'is-google-source' : 'is-bing-source',
      )}
    >
      <NdrPopup
        image={data.popupImage}
        queryParams={allQueryParamsString}
        review={stickyBarData}
      />
      <div className='w-full'>
        <NdrHeader
          className='py-xs layout-safe-area'
          disclaimerText={data.disclosure.text}
          disclaimerTitle={data.disclosure.title}
          homeUrl={homeUrl}
        />
        <div className='bg-secondary'>
          <NdrHero
            data={data}
            queryParams={isNdrLink(data.primaryCta.url) ? allQueryParamsString : ''}
          />
        </div>
      </div>

      <div className='w-full lg:container-ft lg:mx-auto px-lg'>
        <div className='grid layout-grid-gap md:grid-cols-[1.5fr_1fr] lg:grid-cols-[2.5fr_1fr]'>
          <div
            className='flex flex-col flex-1 layout-gap-xs'
            data-cy='ndr-company-cards'
          >
            {orderedReviews.map((review, index) => (
              <Suspense
                fallback={<NdrCompanyCardSkeleton isHighlighted={index === 0} />}
                key={review.companySlug || review.companyName}
              >
                <NdrCompanyCard
                  card={review}
                  isHighlighted={index === 0}
                  onMount={() => setCompanyCardMounted(true)}
                  onResize={runRetreaver}
                  queryParams={isNdrLink(review.ctas.primary.url) ? allQueryParamsString : ''}
                />
              </Suspense>
            ))}
          </div>

          <div className='block'>
            <div className='md:sticky top-4'>
              <TableOfContents
                queryParams={allQueryParamsString}
                reviews={orderedReviews}
                source={isGoogleSource ? 'google' : 'bing'}
              />
            </div>
          </div>
        </div>
      </div>

      <NdrFaq
        className='px-lg'
        faqs={data.faqs}
        title='FAQ'
      />

      <div className='container-content mx-auto layout-gap-md flex flex-col text-center'>
        <h2
          className='typography-level-2 font-semibold text-neutral-900 text-center'
          data-cy='ndr-methodology-title'
        >
          {data?.methodology?.title || ''}
        </h2>
        <DescriptionParser content={data?.methodology?.description || ''} />
      </div>
      <div
        className='absolute'
        ref={stickyTriggerRef}
      />
      <NdrStickyBar
        onMount={() => setStickyBarMounted(true)}
        queryParams={isNdrLink(data.primaryCta.url) ? allQueryParamsString : ''}
        review={stickyBarData}
        stickyButtonPhone={data.defaultPhoneNumber}
        triggerElement={stickyTriggerRef}
      />

      <NdrFooter footerContent={data.footer} />
      {children}
    </div>
  );
};

export { NdrWrapper };
